import React from "react";
import { Link, Navigate } from "react-router-dom";
import TermsModal from "../components/TermsModal";
import BasicInfoStep from "../components/Signup/BasicInfoStep";
import BusinessInfoStep from "../components/Signup/BusinessInfoStep";
import AdditionalInfoStep from "../components/Signup/AdditionalInfoStep";
import AgreementStep from "../components/Signup/AgreementStep";
import useSignupForm from "../hooks/useSignupForm";
import { useSeller } from "../context/SellerContext";
import SellerBusinessInfo from "../components/SellerBusinessInfo";
import "../styles/common.css";

function Signup() {
  const { loading: sellerLoading } = useSeller();
  const {
    seller,
    isSellerSite,
    memberType,
    setMemberType,
    formData,
    error,
    loading,
    validationErrors,
    emailChecked,
    usernameChecked,
    mobileChecked,
    agreements,
    activeModal,
    handleOpenModal,
    handleCloseModal,
    handleChange,
    handlePhoneChange,
    checkUsernameDuplicate,
    checkEmailDuplicate,
    checkMobileDuplicate,
    openAddressSearch,
    handleAllAgreement,
    handleAgreement,
    handleSubmit,
    handleKakaoSignup,
    handleNaverSignup,
  } = useSignupForm();

  // 서브몰이 아닌 경우 회원가입 페이지 접근 불가 → 홈으로 리다이렉트
  if (!sellerLoading && !isSellerSite) {
    return <Navigate to="/" />;
  }

  return (
    <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col items-center justify-center p-6 lg:p-12 font-display">
      <div className="w-full max-w-xl bg-white dark:bg-[#162a1c] border border-[#e7f3eb] dark:border-[#1d3324] rounded-3xl p-8 lg:p-12 shadow-xl shadow-gray-100 dark:shadow-none">

        {/* Header */}
        <div className="text-center mb-10">
          {isSellerSite && seller?.logoUrl ? (
             <img src={seller.logoUrl} alt={seller.shopName} className="h-16 w-auto object-contain mx-auto mb-4" />
          ) : (
             <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-2">🌸 {isSellerSite && seller?.shopName ? seller.shopName : "플라워 플랫폼"}</h1>
          )}
          <p className="text-gray-500 dark:text-gray-400">회원가입</p>
        </div>

        {/* Social Login */}
        <div className="mb-8">
          <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 text-center mb-4 uppercase tracking-wider">간편 회원가입</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className="flex items-center justify-center gap-2 bg-[#FEE500] text-[#3c1e1e] py-3.5 rounded-xl font-bold text-sm hover:brightness-95 transition-all"
              onClick={handleKakaoSignup}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 0 1-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3z" />
              </svg>
              카카오
            </button>
            <button
              type="button"
              className="flex items-center justify-center gap-2 bg-[#03C75A] text-white py-3.5 rounded-xl font-bold text-sm hover:brightness-95 transition-all"
              onClick={handleNaverSignup}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.273 12.845L7.376 0H0v24h7.726V11.156L16.624 24H24V0h-7.727v12.845z" />
              </svg>
              네이버
            </button>
          </div>
        </div>

        <div className="relative mb-8 text-center text-xs text-gray-400 dark:text-gray-500 after:content-[''] after:absolute after:top-1/2 after:left-0 after:w-full after:h-px after:bg-gray-100 dark:after:bg-gray-800 after:-z-10">
          <span className="bg-white dark:bg-[#162a1c] px-4">또는 일반 회원가입</span>
        </div>

        {/* Member Type Tabs */}
        <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl mb-8">
          <button
            type="button"
            className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${memberType === "personal" ? "bg-white dark:bg-[#0d1b12] text-primary-strong shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"}`}
            onClick={() => setMemberType("personal")}
          >
            개인회원
          </button>
          <button
            type="button"
            className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${memberType === "business" ? "bg-white dark:bg-[#0d1b12] text-primary-strong shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"}`}
            onClick={() => setMemberType("business")}
          >
            기업회원
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <div className="bg-red-50 dark:bg-red-900/20 text-red-500 text-sm p-4 rounded-xl text-center font-bold">{error}</div>}

          <BasicInfoStep
            formData={formData}
            onChange={handleChange}
            onPhoneChange={handlePhoneChange}
            validationErrors={validationErrors}
            onUsernameCheck={checkUsernameDuplicate}
            usernameChecked={usernameChecked}
            onEmailCheck={checkEmailDuplicate}
            emailChecked={emailChecked}
            onMobileCheck={checkMobileDuplicate}
            mobileChecked={mobileChecked}
            onAddressSearch={openAddressSearch}
          />

          {memberType === "business" && (
            <BusinessInfoStep
              businessInfo={formData.businessInfo}
              onChange={handleChange}
              validationErrors={validationErrors}
            />
          )}

          <AdditionalInfoStep formData={formData} onChange={handleChange} />

          <AgreementStep
            agreements={agreements}
            onAllAgree={handleAllAgreement}
            onAgreementChange={handleAgreement}
            onOpenModal={handleOpenModal}
            validationErrors={validationErrors}
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white py-4 rounded-xl font-bold text-base hover:bg-black active:scale-[0.98] transition-all shadow-lg shadow-gray-200 dark:shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "가입 처리 중..." : "회원가입"}
          </button>

          <div className="text-center">
            <Link to="/login" className="text-sm font-bold text-gray-500 hover:text-primary-strong transition-colors">
                이미 계정이 있으신가요? 로그인
            </Link>
          </div>
        </form>
      </div>

      {activeModal && (
        <TermsModal
            isOpen={!!activeModal}
            onClose={handleCloseModal}
            initialTab={activeModal}
        />
      )}

      {isSellerSite && (
          <SellerBusinessInfo
            seller={seller}
            align="center"
            className="mt-8 text-xs text-gray-500 max-w-lg"
          />
      )}
    </div>
  );
}

export default Signup;
